/**
 * Renders an array of FormField definitions into employee-facing
 * inputs.
 *
 * State is owned by the caller — this component is a pure controlled
 * surface. The parent passes ``data`` + ``onChange`` and gets back
 * partial-update events as the user types.
 *
 * Read-only mode (``disabled``) is used for:
 *   * Admin viewing a submitted form
 *   * Employee viewing their own already-submitted form
 *   * Preview modal showing how a form will look
 */
import type { ChangeEvent } from "react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SignaturePad } from "./SignaturePad";
import type { FormField } from "./types";

interface FormRendererProps {
  schema: FormField[];
  data: Record<string, unknown>;
  onChange: (id: string, value: unknown) => void;
  disabled?: boolean;
}

const STATE_OPTIONS = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatSSN(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 9);
  if (digits.length < 4) return digits;
  if (digits.length < 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function isMaskedSSN(value: unknown): boolean {
  return typeof value === "string" && value.startsWith("***");
}

export function FormRenderer({
  schema,
  data,
  onChange,
  disabled = false,
}: FormRendererProps) {
  return (
    <div className="space-y-5">
      {schema.map((field) => (
        <FieldRow
          key={field.id}
          field={field}
          value={data[field.id]}
          onChange={(v) => onChange(field.id, v)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function FieldRow({
  field,
  value,
  onChange,
  disabled,
}: {
  field: FormField;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled: boolean;
}) {
  const { id, type, label, required, help_text } = field;
  const labelEl = label ? (
    <label htmlFor={id} className="block text-sm font-medium text-text-primary mb-1">
      {label}
      {required && <span className="text-danger ml-1">*</span>}
    </label>
  ) : null;
  const helpEl = help_text ? (
    <p className="text-xs text-text-muted mt-1">{help_text}</p>
  ) : null;

  // Section header — purely visual, not bound to data.
  if (type === "section_header") {
    return (
      <div className="border-b border-border/40 pb-1">
        <h3 className="text-base font-semibold text-text-primary">
          {label || "Section"}
        </h3>
        {help_text && (
          <p className="text-xs text-text-muted mt-1">{help_text}</p>
        )}
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div>
        {labelEl}
        <textarea
          id={id}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={4}
          className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
        />
        {helpEl}
      </div>
    );
  }

  if (type === "date") {
    return (
      <div>
        {labelEl}
        <Input
          id={id}
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        {helpEl}
      </div>
    );
  }

  if (type === "select") {
    const options = field.options ?? [];
    return (
      <div>
        {labelEl}
        <Select
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          options={[
            { value: "", label: "— Choose one —" },
            ...options.map((o) => ({ value: o, label: o })),
          ]}
        />
        {helpEl}
      </div>
    );
  }

  if (type === "checkbox") {
    return (
      <div>
        <label className="inline-flex items-center gap-2 text-sm text-text-primary cursor-pointer">
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className="h-4 w-4 rounded border-border accent-accent"
          />
          {label}
          {required && <span className="text-danger">*</span>}
        </label>
        {helpEl}
      </div>
    );
  }

  if (type === "checkbox_group") {
    const options = field.options ?? [];
    const values = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div>
        {labelEl}
        <div className="space-y-1.5">
          {options.map((opt) => {
            const checked = values.includes(opt);
            return (
              <label
                key={opt}
                className="inline-flex items-center gap-2 text-sm text-text-primary cursor-pointer w-full"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    onChange(
                      e.target.checked
                        ? [...values, opt]
                        : values.filter((v) => v !== opt),
                    );
                  }}
                  disabled={disabled}
                  className="h-4 w-4 rounded border-border accent-accent"
                />
                {opt}
              </label>
            );
          })}
        </div>
        {helpEl}
      </div>
    );
  }

  if (type === "radio") {
    const options = field.options ?? [];
    return (
      <div>
        {labelEl}
        <div className="space-y-1.5">
          {options.map((opt) => (
            <label
              key={opt}
              className="inline-flex items-center gap-2 text-sm text-text-primary cursor-pointer w-full"
            >
              <input
                type="radio"
                name={id}
                value={opt}
                checked={value === opt}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(e.target.value)
                }
                disabled={disabled}
                className="h-4 w-4 border-border accent-accent"
              />
              {opt}
            </label>
          ))}
        </div>
        {helpEl}
      </div>
    );
  }

  if (type === "phone") {
    return (
      <div>
        {labelEl}
        <Input
          id={id}
          type="tel"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(formatPhone(e.target.value))}
          disabled={disabled}
          placeholder="(555) 123-4567"
          inputMode="tel"
        />
        {helpEl}
      </div>
    );
  }

  if (type === "email") {
    return (
      <div>
        {labelEl}
        <Input
          id={id}
          type="email"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="you@example.com"
        />
        {helpEl}
      </div>
    );
  }

  if (type === "address") {
    const addr =
      (value as { street?: string; city?: string; state?: string; zip?: string } | undefined) ??
      { street: "", city: "", state: "", zip: "" };
    const update = (key: string, v: string) =>
      onChange({ ...addr, [key]: v });
    return (
      <div>
        {labelEl}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="md:col-span-2">
            <Input
              placeholder="Street address"
              value={addr.street ?? ""}
              onChange={(e) => update("street", e.target.value)}
              disabled={disabled}
            />
          </div>
          <Input
            placeholder="City"
            value={addr.city ?? ""}
            onChange={(e) => update("city", e.target.value)}
            disabled={disabled}
          />
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <Select
                value={addr.state ?? ""}
                onChange={(e) => update("state", e.target.value)}
                disabled={disabled}
                options={[
                  { value: "", label: "State" },
                  ...STATE_OPTIONS.map((s) => ({ value: s, label: s })),
                ]}
              />
            </div>
            <div className="col-span-2">
              <Input
                placeholder="ZIP"
                value={addr.zip ?? ""}
                onChange={(e) =>
                  update("zip", e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                disabled={disabled}
                inputMode="numeric"
              />
            </div>
          </div>
        </div>
        {helpEl}
      </div>
    );
  }

  if (type === "ssn") {
    // The server returns SSN values masked (``***-**-1234``) for
    // privacy. We don't show that pattern in the input itself —
    // typing into a "***-**-1234" box would feel broken — so the
    // input renders empty, the help text reveals the saved last-4,
    // and the user can either leave it untouched (server keeps the
    // existing encrypted value) or type a new SSN to overwrite.
    const masked = isMaskedSSN(value);
    const inputValue = masked ? "" : typeof value === "string" ? value : "";
    return (
      <div>
        {labelEl}
        <Input
          id={id}
          type="text"
          value={inputValue}
          onChange={(e) => onChange(formatSSN(e.target.value))}
          disabled={disabled}
          placeholder={
            masked && typeof value === "string"
              ? value /* show masked as placeholder */
              : "123-45-6789"
          }
          autoComplete="off"
          inputMode="numeric"
        />
        <p className="text-xs text-text-muted mt-1">
          {masked
            ? "Already saved. Type a new SSN above to overwrite, or leave blank to keep the existing one."
            : help_text ||
              "Encrypted at rest. Only the last 4 digits are visible after submission."}
        </p>
      </div>
    );
  }

  if (type === "signature") {
    return (
      <div>
        {labelEl}
        <SignaturePad
          value={typeof value === "string" ? value : null}
          onChange={onChange}
          disabled={disabled}
        />
        {helpEl}
      </div>
    );
  }

  if (type === "file_upload") {
    const fileMeta = (value as { filename?: string; size?: number } | string | undefined);
    return (
      <div>
        {labelEl}
        <input
          id={id}
          type="file"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) {
              onChange(null);
              return;
            }
            // v1: store filename + size only. Full upload pipeline
            // hooks into the existing /uploads endpoint — wired in a
            // follow-up; the form submit still works because the
            // server treats this as opaque metadata.
            onChange({ filename: f.name, size: f.size });
          }}
          disabled={disabled}
          className="block w-full text-sm text-text-muted file:mr-3 file:rounded-md file:border-0 file:bg-accent/10 file:px-3 file:py-1.5 file:text-accent file:font-medium hover:file:bg-accent/20"
        />
        {fileMeta && typeof fileMeta === "object" && fileMeta.filename && (
          <p className="text-xs text-text-muted mt-1">
            Attached: {fileMeta.filename}
          </p>
        )}
        {helpEl}
      </div>
    );
  }

  // Default = single-line text.
  return (
    <div>
      {labelEl}
      <Input
        id={id}
        type="text"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
      {helpEl}
    </div>
  );
}

export default FormRenderer;
